import { queryClient } from "@istari-frontend/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  appendConversationMessage,
  createConversation,
  listConversationMessages,
  listConversations,
} from "~/ai-chat/data-source/conversations-api";
import type { ConversationMessage } from "~/ai-chat/model/conversation";

export const conversationKeys = {
  all: ["ai-conversations"] as const,
  list: () => [...conversationKeys.all, "list"] as const,
  messages: (conversationId: string) => [...conversationKeys.all, conversationId, "messages"] as const,
};

export function useConversations() {
  return useQuery({
    queryKey: conversationKeys.list(),
    queryFn: listConversations,
  });
}

export function useCreateConversation() {
  return useMutation({
    mutationFn: (title: string | null) => createConversation(title),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.list() });
    },
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: conversationKeys.messages(conversationId ?? ""),
    queryFn: () => listConversationMessages(conversationId ?? ""),
    enabled: conversationId !== null,
  });
}

type AppendMessageParams = {
  conversationId: string;
  role: ConversationMessage["role"];
  content: string;
};

export function useAppendConversationMessage() {
  return useMutation({
    mutationFn: ({ conversationId, role, content }: AppendMessageParams) =>
      appendConversationMessage(conversationId, role, content),
    onSuccess: (_message, { conversationId }) => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.list() });
    },
  });
}
